import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, LabelList, Cell,
    LineChart, Line, Legend
} from 'recharts';
import { FaTicketAlt, FaCalendarAlt, FaEuroSign, FaIdCard, FaSearch, FaChartBar } from 'react-icons/fa';
import './TicketAnalytics.css';
import API_BASE_URL from '../../config/api';

// ── Constants ─────────────────────────────────────────────────────────────────
const MONTH_NAMES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
    'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const PALETTE = {
    blue: ['#1e3c72', '#2a5298', '#2575fc', '#4a90e2'],
    coral: ['#ff7e5f', '#feb47b', '#ff6b6b', '#ffa07a'],
    green: ['#11998e', '#38ef7d', '#43e97b', '#56ab2f'],
    purple: ['#6a11cb', '#8b5cf6', '#a855f7', '#c084fc'],
    teal: ['#0072ff', '#00c6ff', '#36d1dc', '#5b86e5'],
    yellow: ['#f7971e', '#ffd200', '#f6d365', '#fda085'],
};

// ── SVG Gradient Defs ─────────────────────────────────────────────────────────
const GradientDefs = () => (
    <defs>
        <linearGradient id="gradBlue" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2575fc" stopOpacity={0.9} />
            <stop offset="100%" stopColor="#1e3c72" stopOpacity={0.7} />
        </linearGradient>
        <linearGradient id="gradCoral" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ff7e5f" stopOpacity={0.9} />
            <stop offset="100%" stopColor="#feb47b" stopOpacity={0.7} />
        </linearGradient>
        <linearGradient id="gradGreen" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#43e97b" stopOpacity={0.9} />
            <stop offset="100%" stopColor="#11998e" stopOpacity={0.7} />
        </linearGradient>
        <linearGradient id="gradPurple" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#a855f7" stopOpacity={0.9} />
            <stop offset="100%" stopColor="#6a11cb" stopOpacity={0.7} />
        </linearGradient>
        <linearGradient id="gradTeal" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00c6ff" stopOpacity={0.9} />
            <stop offset="100%" stopColor="#0072ff" stopOpacity={0.7} />
        </linearGradient>
        <linearGradient id="gradYellow" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f7971e" stopOpacity={0.9} />
            <stop offset="100%" stopColor="#ffd200" stopOpacity={0.7} />
        </linearGradient>
        {/* Horizontal gradients for horizontal bar charts */}
        <linearGradient id="gradBlueH" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#1e3c72" stopOpacity={0.85} />
            <stop offset="100%" stopColor="#2575fc" stopOpacity={0.95} />
        </linearGradient>
        <linearGradient id="gradGreenH" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#11998e" stopOpacity={0.85} />
            <stop offset="100%" stopColor="#43e97b" stopOpacity={0.95} />
        </linearGradient>
        <linearGradient id="gradYellowH" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#f7971e" stopOpacity={0.85} />
            <stop offset="100%" stopColor="#ffd200" stopOpacity={0.95} />
        </linearGradient>
    </defs>
);

// ── Custom Tooltip ────────────────────────────────────────────────────────────
const TicketTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0];
    const color = item.fill || item.color || 'var(--color-primary)';
    return (
        <div className="ta-tooltip">
            <div className="ta-tooltip-bar" style={{ background: color }} />
            <div className="ta-tooltip-body">
                <span className="ta-tooltip-label">{label || item.name}</span>
                <span className="ta-tooltip-value" style={{ color }}>{item.value}</span>
                {payload[1] && (
                    <span className="ta-tooltip-sub">€{payload[1].value?.toFixed(2)}</span>
                )}
            </div>
        </div>
    );
};

const MonthTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="ta-tooltip ta-tooltip-multi">
            <div className="ta-tooltip-bar" style={{ background: 'var(--color-primary)' }} />
            <div className="ta-tooltip-body">
                <span className="ta-tooltip-label">{label}</span>
                {payload.map((p, i) => (
                    <span key={i} className="ta-tooltip-row">
                        <span className="ta-dot" style={{ background: p.color }} />
                        <span>{p.name}:</span>
                        <strong>{p.value}</strong>
                    </span>
                ))}
            </div>
        </div>
    );
};

// ── Skeleton ──────────────────────────────────────────────────────────────────
const TicketSkeleton = () => (
    <div className="ta-skeleton-wrap">
        <div className="ta-kpi-grid">
            {[1, 2, 3, 4].map(i => (
                <div key={i} className="ta-kpi-card skeleton-card">
                    <div className="sk sk-icon" />
                    <div className="sk-info">
                        <div className="sk sk-label" />
                        <div className="sk sk-value" />
                    </div>
                </div>
            ))}
        </div>
        <div className="ta-charts-row">
            <div className="ta-chart-block sk-block" />
            <div className="ta-chart-block sk-block" />
        </div>
    </div>
);

// ── Main Component ─────────────────────────────────────────────────────────────
const TicketAnalytics = () => {
    const currentYear = new Date().getFullYear();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedYear, setSelectedYear] = useState(currentYear.toString());
    const [gameSearch, setGameSearch] = useState('');
    const [gameFilter, setGameFilter] = useState('all');
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 640);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        fetchData(selectedYear);
    }, [selectedYear]);

    const fetchData = async (year) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL}/api/ClubAnalytics/tickets`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { year }
            });
            setData(res.data);
        } catch (e) {
            console.error(e);
            setError('Erro ao carregar estatísticas de bilhetes.');
        } finally {
            setLoading(false);
        }
    };

    const availableYears = useMemo(() => {
        if (!data?.byYear?.length) return [currentYear.toString()];
        return data.byYear.map(y => y.year.toString());
    }, [data, currentYear]);

    const uniqueSports = useMemo(() => {
        if (!data?.ticketsPerGame) return ['Todas'];
        const sports = [...new Set(data.ticketsPerGame.map(g => g.sportName))];
        return ['Todas', ...sports.sort()];
    }, [data]);

    const filteredGames = useMemo(() => {
        if (!data?.ticketsPerGame) return [];
        return data.ticketsPerGame
            .filter(g => {
                const matchSport = gameFilter === 'all' || gameFilter === 'Todas' || g.sportName === gameFilter;
                const matchSearch = g.eventTitle.toLowerCase().includes(gameSearch.toLowerCase());
                return matchSport && matchSearch;
            })
            .slice(0, 20); // top 20
    }, [data, gameFilter, gameSearch]);

    const monthlyData = useMemo(() => {
        if (!data?.byMonth) return [];
        const map = {};
        data.byMonth.forEach(m => { map[m.month] = m; });
        return Array.from({ length: 12 }, (_, i) => ({
            name: MONTH_NAMES[i],
            'Jogo': map[i + 1]?.gameTickets ?? 0,
            'Passe Anual': map[i + 1]?.annualPasses ?? 0,
            total: map[i + 1]?.total ?? 0,
        }));
    }, [data]);

    const gameChartHeight = Math.max(360, filteredGames.length * 44);

    if (loading) return <TicketSkeleton />;
    if (error) return <div className="ta-error">{error}</div>;
    if (!data) return null;

    const { overview, annualPassesBySport, byYear } = data;

    const kpiCards = [
        {
            icon: <FaTicketAlt />,
            label: 'Total de Bilhetes',
            value: overview.totalThisYear,
            accent: PALETTE.blue[2],
            sub: `${selectedYear}`
        },
        {
            icon: <FaCalendarAlt />,
            label: 'Bilhetes de Jogo',
            value: overview.gameTicketsThisYear,
            accent: PALETTE.green[0],
            sub: 'eventos individuais'
        },
        {
            icon: <FaIdCard />,
            label: 'Passes Anuais',
            value: overview.annualPassesThisYear,
            accent: PALETTE.yellow[0],
            sub: 'bilhetes época'
        },
        {
            icon: <FaEuroSign />,
            label: 'Receita de Bilhetes',
            value: `€${overview.revenueThisYear.toFixed(2)}`,
            accent: PALETTE.purple[0],
            sub: `${selectedYear}`
        },
    ];

    return (
        <div className="ta-container">

            {/* ── Year Filter ── */}
            <div className="ta-year-bar">
                <FaCalendarAlt className="ta-year-icon" />
                <span className="ta-year-label">Ano:</span>
                <div className="ta-year-pills">
                    {availableYears.map(y => (
                        <button
                            key={y}
                            className={`ta-year-pill ${selectedYear === y ? 'active' : ''}`}
                            onClick={() => setSelectedYear(y)}
                        >
                            {y}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── KPI Cards ── */}
            <div className="ta-kpi-grid">
                {kpiCards.map((card, i) => (
                    <div
                        key={i}
                        className="ta-kpi-card"
                        style={{ animationDelay: `${i * 80}ms` }}
                    >
                        <div className="ta-kpi-icon" style={{ background: card.accent + '18', color: card.accent }}>
                            {card.icon}
                        </div>
                        <div className="ta-kpi-info">
                            <span className="ta-kpi-label">{card.label}</span>
                            <span className="ta-kpi-value" style={{ color: card.accent }}>{card.value}</span>
                            <span className="ta-kpi-sub">{card.sub}</span>
                        </div>
                        <div className="ta-kpi-accent" style={{ background: card.accent }} />
                    </div>
                ))}
            </div>

            {/* ── Monthly Trend ── */}
            <div className="ta-chart-block ta-full-width" style={{ animationDelay: '350ms' }}>
                <div className="ta-chart-header">
                    <div className="ta-badge" style={{ background: 'rgba(0, 51, 128, 0.08)', color: 'var(--color-primary)' }}>Tendência</div>
                    <h3>Bilhetes por Mês — {selectedYear}</h3>
                </div>
                <div className="ta-chart-area">
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={monthlyData} margin={{ top: 16, right: 24, left: 0, bottom: 0 }}>
                            <GradientDefs />
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                            <XAxis
                                dataKey="name"
                                tick={{ fill: '#718096', fontSize: 12 }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                tick={{ fill: '#a0aec0', fontSize: 11 }}
                                axisLine={false}
                                tickLine={false}
                                allowDecimals={false}
                            />
                            <Tooltip content={<MonthTooltip />} cursor={{ fill: 'rgba(0, 51, 128, 0.05)' }} />
                            <Legend
                                iconType="circle"
                                iconSize={8}
                                wrapperStyle={{ fontSize: 12, paddingTop: 10 }}
                            />
                            <Bar dataKey="Jogo" stackId="a" fill="url(#gradBlue)" radius={[0, 0, 0, 0]} barSize={30} />
                            <Bar dataKey="Passe Anual" stackId="a" fill="url(#gradYellow)" radius={[6, 6, 0, 0]} barSize={30} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* ── Two charts side by side ── */}
            <div className="ta-charts-row">

                {/* Bilhetes por Jogo */}
                <div className="ta-chart-block" style={{ animationDelay: '450ms' }}>
                    <div className="ta-chart-header">
                        <div className="ta-badge" style={{ background: 'rgba(17, 153, 142, 0.08)', color: '#11998e' }}>Jogos</div>
                        <h3>Bilhetes por Jogo / Evento</h3>
                    </div>
                    <div className="ta-chart-filters">
                        <div className="ta-search-wrap">
                            <FaSearch className="ta-search-icon" />
                            <input
                                type="text"
                                placeholder="Pesquisar evento…"
                                value={gameSearch}
                                onChange={e => setGameSearch(e.target.value)}
                                className="ta-search-input"
                            />
                        </div>
                        <select
                            value={gameFilter}
                            onChange={e => setGameFilter(e.target.value)}
                            className="ta-filter-select"
                        >
                            {uniqueSports.map(s => (
                                <option key={s} value={s === 'Todas' ? 'all' : s}>{s === 'all' ? 'Todas' : s}</option>
                            ))}
                        </select>
                    </div>
                    {filteredGames.length > 0 ? (
                        <div className="ta-hbar-scroll" style={{ height: gameChartHeight }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={filteredGames.map(g => ({ name: g.eventTitle, count: g.count, revenue: g.revenue }))}
                                    layout="vertical"
                                    margin={{ top: 4, right: isMobile ? 32 : 64, left: isMobile ? 0 : 8, bottom: 4 }}
                                >
                                    <GradientDefs />
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                                    <XAxis type="number" tick={{ fill: '#a0aec0', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                    <YAxis
                                        dataKey="name"
                                        type="category"
                                        width={isMobile ? 95 : 180}
                                        tick={{ fill: '#4a5568', fontSize: isMobile ? 10 : 12 }}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <Tooltip content={<TicketTooltip />} cursor={{ fill: 'rgba(17,153,142,0.05)' }} />
                                    <Bar dataKey="count" name="Bilhetes" fill="url(#gradGreenH)" radius={[0, 8, 8, 0]} barSize={28} animationDuration={900}>
                                        <LabelList dataKey="count" position="right" style={{ fill: '#4a5568', fontSize: isMobile ? 10 : 12, fontWeight: 700 }} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div className="ta-empty">Nenhum evento encontrado.</div>
                    )}
                </div>

                {/* Passes Anuais por Desporto */}
                <div className="ta-chart-block" style={{ animationDelay: '550ms' }}>
                    <div className="ta-chart-header">
                        <div className="ta-badge" style={{ background: 'rgba(247, 151, 30, 0.08)', color: '#f7971e' }}>Passes Anuais</div>
                        <h3>Passes Anuais Vendidos por Modalidade</h3>
                    </div>
                    {annualPassesBySport.length > 0 ? (
                        <div className="ta-hbar-scroll" style={{ height: Math.max(300, annualPassesBySport.length * 60) }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={annualPassesBySport.map(s => ({ name: s.sport, count: s.count, revenue: s.revenue }))}
                                    layout="vertical"
                                    margin={{ top: 4, right: isMobile ? 32 : 64, left: isMobile ? 0 : 8, bottom: 4 }}
                                >
                                    <GradientDefs />
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                                    <XAxis type="number" tick={{ fill: '#a0aec0', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                    <YAxis
                                        dataKey="name"
                                        type="category"
                                        width={isMobile ? 80 : 150}
                                        tick={{ fill: '#4a5568', fontSize: isMobile ? 11 : 13, fontWeight: 600 }}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <Tooltip content={<TicketTooltip />} cursor={{ fill: 'rgba(247,151,30,0.05)' }} />
                                    <Bar dataKey="count" name="Passes" fill="url(#gradYellowH)" radius={[0, 10, 10, 0]} barSize={36} animationDuration={900}>
                                        <LabelList dataKey="count" position="right" style={{ fill: '#4a5568', fontSize: isMobile ? 11 : 13, fontWeight: 700 }} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div className="ta-empty">Nenhum passe anual vendido.</div>
                    )}
                </div>
            </div>

            {/* ── Histórico Anual Table ── */}
            {byYear.length > 0 && (
                <div className="ta-chart-block ta-full-width" style={{ animationDelay: '650ms' }}>
                    <div className="ta-chart-header">
                        <div className="ta-badge" style={{ background: 'rgba(106, 17, 203, 0.08)', color: '#6a11cb' }}>Histórico</div>
                        <h3>Resumo Anual de Bilhetes</h3>
                    </div>
                    {!isMobile ? (
                        <div className="ta-table-wrap">
                            <table className="ta-table">
                                <thead>
                                    <tr>
                                        <th>Ano</th>
                                        <th>Total Bilhetes</th>
                                        <th>Bilhetes de Jogo</th>
                                        <th>Passes Anuais</th>
                                        <th>Receita Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {byYear.map((row, i) => (
                                        <tr
                                            key={row.year}
                                            className={row.year.toString() === selectedYear ? 'ta-row-active' : ''}
                                        >
                                            <td className="ta-cell-year">
                                                <span className="ta-year-chip">{row.year}</span>
                                            </td>
                                            <td>
                                                <div className="ta-cell-bar-wrap">
                                                    <span className="ta-cell-num">{row.total}</span>
                                                    <div className="ta-mini-bar">
                                                        <div
                                                            className="ta-mini-fill"
                                                            style={{
                                                                width: `${Math.min(100, (row.total / Math.max(...byYear.map(y => y.total))) * 100)}%`,
                                                                background: 'var(--color-primary)'
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </td>
                                            <td><span className="ta-pill ta-pill-blue">{row.gameTickets}</span></td>
                                            <td><span className="ta-pill ta-pill-orange">{row.annualPasses}</span></td>
                                            <td className="ta-cell-revenue">€{row.revenue.toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="ta-mobile-cards">
                            {byYear.map((row, i) => (
                                <div
                                    key={row.year}
                                    className={`ta-mobile-card ${row.year.toString() === selectedYear ? 'ta-card-active' : ''}`}
                                    onClick={() => setSelectedYear(row.year.toString())}
                                >
                                    <div className="ta-card-top">
                                        <span className="ta-card-year-chip">{row.year}</span>
                                        <span className="ta-card-revenue">€{row.revenue.toFixed(2)}</span>
                                    </div>
                                    <div className="ta-card-body">
                                        <div className="ta-card-metric">
                                            <span className="ta-card-metric-label">Total Bilhetes</span>
                                            <span className="ta-card-metric-value">{row.total}</span>
                                        </div>
                                        <div className="ta-card-sub-grid">
                                            <div className="ta-card-sub-item">
                                                <span className="ta-card-sub-label">De Jogo:</span>
                                                <span className="ta-pill ta-pill-blue">{row.gameTickets}</span>
                                            </div>
                                            <div className="ta-card-sub-item">
                                                <span className="ta-card-sub-label">Anuais:</span>
                                                <span className="ta-pill ta-pill-orange">{row.annualPasses}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="ta-card-footer">
                                        <div className="ta-mini-bar" style={{ maxWidth: '100%' }}>
                                            <div
                                                className="ta-mini-fill"
                                                style={{
                                                    width: `${Math.min(100, (row.total / Math.max(...byYear.map(y => y.total))) * 100)}%`,
                                                    background: 'var(--color-primary)'
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TicketAnalytics;
